import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { InboxDto } from '@app/core/model';
import { InboundFaxApiService, LookupService, NotificationService } from '@app/core/service';
import { FormDialogComponent } from '@app/shared/components';
import { FormDialogConfig, InboundFaxesTableModel } from '@app/shared/models';
import { DialogConfig } from '@app/shared/utility';
import { ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { of } from 'rxjs';
import { InboundFaxesService } from './inbound-faxes.service';

@Injectable()
export class FaxUserService {
  private readonly confirmationService = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogService = inject(DialogService);
  private readonly inboundFaxApiService = inject(InboundFaxApiService);
  private readonly inboundFaxesService = inject(InboundFaxesService);
  private readonly notificationService = inject(NotificationService);
  private readonly lookupService = inject(LookupService);

  public async handleClick(target: InboundFaxesTableModel, inbox: InboxDto, currentUserId: string): Promise<void> {
    if (!target.assignedUserId) {
      this.openAssignDialog(target, inbox, currentUserId);
    } else {
      this.openRemoveDialog(target, inbox);
    }
  }

  private async openAssignDialog(target: InboundFaxesTableModel, inbox: InboxDto, currentUserId: string): Promise<void> {
    const users = await this.lookupService.getUsersDropdown(currentUserId);
    const formDialogConfigs: FormDialogConfig[] = [
      {
        label: 'User',
        type: 'dropdown',
        formControlName: 'user',
        options: users,
        validators: [Validators.required],
      },
    ];

    this.dialogService
      .open(
        FormDialogComponent,
        DialogConfig('Assign User', {
          closable: true,
          submitLabel: 'Assign',
          formDialogConfigs: of(formDialogConfigs)
        })
      )
      .onClose.subscribe((res: object) => {
        if (res) {
          this.updateAssignedUser(target.inboundFaxId, res['user'], inbox);
        }
      });
  }

  private openRemoveDialog(target: InboundFaxesTableModel, inbox: InboxDto): void {
    this.confirmationService.confirm({
      closable: true,
      acceptLabel: 'Remove',
      rejectLabel: 'Cancel',
      header: 'Remove Assigned User',
      message: 'Are you sure that you want to remove the assigned user?',
      accept: () => {
        this.updateAssignedUser(target.inboundFaxId, null, inbox);
      },
      reject: () => {},
    });
  }

  private updateAssignedUser(inboundFaxId: string, updateId: string, inbox: InboxDto) {
    this.inboundFaxesService.isLoadingTableData$.next(true);

    this.inboundFaxApiService
      .updateUser(inboundFaxId, { updateId })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.notificationService.showSuccess(
            'Successfully Updated User',
            'User has been updated to the fax record.'
          );
          this.inboundFaxesService.reload(inbox);
        },
        error: () => {
          this.notificationService.showError(
            'Unable to Assign User',
            'There was an issue assigning the user. Please try again later.'
          );
          this.inboundFaxesService.isLoadingTableData$.next(false);
        },
      });
  }
}
